import Phaser from "phaser";
import {
  BACKGROUND_PRESETS,
  DEFAULT_BACKGROUND_ID,
  NATIVE_HEIGHT,
  NATIVE_WIDTH,
  PALETTE,
  UGC_CYAN,
} from "../config";
import { InputService } from "../services/InputService";
import type { SessionSummary } from "../types";
import { t } from "../i18n";
import type { StringKey } from "../i18n";

/**
 * View 8 — End-of-Session Summary (ui-sketches §8).
 *
 * Renders the session summary received from RunnerScene and waits for the
 * operator to return to the launcher. v0.3 does not write the JSONL/CSV
 * export files to disk (PRD §10) — that's a Tauri filesystem-bridge job
 * scheduled for v0.4. For now the telemetry buffer is already in
 * localStorage at session end (see TelemetryService.flush()).
 *
 * Only fires on a real session completion (timeup or hp_down); a paused/
 * aborted run goes straight back to the launcher to keep the summary
 * representative of a completed study run.
 */
export class EndScene extends Phaser.Scene {
  private controls!: InputService;
  private summary!: SessionSummary;

  constructor() {
    super("end");
  }

  init(data: SessionSummary): void {
    this.summary = data;
  }

  create(): void {
    this.controls = new InputService(this);
    this.cameras.main.setBackgroundColor(PALETTE.hudPanel);

    const cx = NATIVE_WIDTH / 2;
    this.add
      .text(cx, 18, t("end.header"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);

    const endReasonLabel =
      this.summary.endReason === "hp_down" ? t("end.reason.hp_down") : t("end.reason.timeup");
    this.add
      .text(cx, 30, endReasonLabel, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hp),
      })
      .setOrigin(0.5, 0.5);

    const meta = t("end.meta", {
      variant: this.summary.variant,
      participant: this.summary.participantId,
    });
    this.add
      .text(cx, 44, meta, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    this.add
      .text(cx, 54, t("end.duration", { sec: this.summary.durationSec }), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    // Stats panel.
    const px = 40;
    const py = 70;
    const pw = NATIVE_WIDTH - 80;
    const ph = 114;
    this.add
      .rectangle(px, py, pw, ph, PALETTE.sky)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);

    const bOnly = t("end.b_only");
    const statRow = (key: StringKey, value: string) => this.dotLeader(t(key), value);
    const lines = [
      statRow("end.stat.geo", `${this.summary.geoCollected}`),
      statRow(
        "end.stat.gegner",
        `${this.summary.gegnerDefeated} / ${this.summary.gegnerEncounters} ${t("end.stat.gegner.suffix")}`,
      ),
      statRow("end.stat.attacks", `${this.summary.attacksLanded}`),
      statRow("end.stat.double_jumps", `${this.summary.doubleJumpsPerformed}`),
      statRow(
        "end.stat.sidequests",
        `${this.summary.sidequestsAccepted} / ${this.summary.sidequestsCleared}`,
      ),
      statRow("end.stat.inventory", `${this.summary.inventoryItems}`),
      statRow(
        "end.stat.hcb",
        `${this.summary.heartsCollected} / ${this.summary.crystalsCollected} / ${this.summary.bootsCollected}`,
      ),
      statRow("end.stat.towers", `${this.summary.towersAuthored}  ${bOnly}`),
      statRow("end.stat.background_uploads", `${this.summary.backgroundUploadsApplied}  ${bOnly}`),
    ];
    lines.forEach((line, i) => {
      this.add
        .text(px + 8, py + 8 + i * 11, line, {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.toHex(PALETTE.hudText),
        })
        .setOrigin(0, 0);
    });

    // UGC background readout — only when a non-default preset was active.
    if (
      this.summary.variant === "B" &&
      this.summary.backgroundId !== DEFAULT_BACKGROUND_ID
    ) {
      const presetLabel =
        BACKGROUND_PRESETS.find((p) => p.id === this.summary.backgroundId)?.label ??
        this.summary.backgroundId;
      this.add
        .text(cx, py + ph + 6, t("end.ugc.background", { label: presetLabel }), {
          fontFamily: "monospace",
          fontSize: "7px",
          color: this.toHex(UGC_CYAN),
        })
        .setOrigin(0.5, 0);
    }

    this.add
      .text(cx, NATIVE_HEIGHT - 36, t("end.flushed"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    const returnBtn = this.add
      .rectangle(cx, NATIVE_HEIGHT - 18, 130, 16, PALETTE.geo)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.heroOutline);
    this.add
      .text(cx, NATIVE_HEIGHT - 18, t("end.return_to_launcher"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: "#000000",
      })
      .setOrigin(0.5, 0.5);
    returnBtn.setInteractive({ useHandCursor: true });
    returnBtn.on("pointerdown", () => this.scene.start("launcher"));
  }

  override update(): void {
    const s = this.controls.read();
    if (s.jumpPressed || s.interactPressed || s.pausePressed) {
      this.scene.start("launcher");
    }
  }

  private toHex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }

  /**
   * Render a "label .......... value" stat row by padding the label with
   * dots out to a fixed column. Column chosen so the longest expected EN
   * label still has at least one separator dot before the value.
   */
  private dotLeader(label: string, value: string): string {
    const col = 30;
    const padding = Math.max(1, col - label.length);
    return `${label} ${".".repeat(padding)} ${value}`;
  }
}
